'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Logo from '@/images/logo_written.svg';
import { useDarkMode } from '@/hooks/useDarkMode';
import type { LinkFormat } from '@/components/common/NavLinks';

const LightDarkModeSwitch = dynamic(
  () => import('@/components/feature/theme/LightDarkModeSwitch'),
  { ssr: false }
);

interface HeaderClientProps {
  navLinks: LinkFormat[];
}

export function MobileMenu({ navLinks }: HeaderClientProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDarkMode } = useDarkMode();

  return (
    <>
      <div className="flex lg:hidden">
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
        >
          <span className="sr-only">Open main menu</span>
          <Bars3Icon aria-hidden="true" className="h-6 w-6" />
        </button>
      </div>

      <Dialog as="div" open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-10" />
        <DialogPanel
          className={`fixed inset-y-0 right-0 z-10 w-full overflow-y-auto ${
            isDarkMode ? 'bg-secondary text-primary' : 'bg-white text-gray-900'
          } px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10`}
        >
          <div className="flex items-center justify-between">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">Xronos Inc.</span>
              <Logo className="h-6 w-auto text-primary fill-current" />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {navLinks.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="-mx-3 block rounded-lg px-3 py-3 text-base font-semibold leading-7 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </>
  );
}

export function HeaderActions() {
  return (
    <div className="hidden lg:flex items-center gap-4">
      <a
        href="https://github.com/xronos-inc/xronos"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open Xronos GitHub repository"
        className="group inline-flex h-6 w-6 items-center justify-center rounded-full
                   text-black dark:text-white
                   ring-2 ring-transparent ring-offset-2 ring-offset-transparent
                   transition-all
                   hover:ring-indigo-500 hover:ring-offset-white
                   dark:hover:ring-indigo-500 dark:hover:ring-offset-secondary
                   focus:outline-none
                   focus-visible:ring-indigo-500 focus-visible:ring-offset-white
                   dark:focus-visible:ring-indigo-500 dark:focus-visible:ring-offset-secondary"
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="h-6 w-6"
          focusable="false"
        >
          <circle cx="12" cy="12" r="12" fill="#000000" />
          <path
            fill="#FFFFFF"
            d="M12 .5C5.65.5.5 5.65.5 12c0 5.1 3.29 9.42 7.86 10.95.58.11.79-.25.79-.56 0-.31-.01-1.13-.02-2.22-3.19.7-3.87-1.57-3.87-1.57-.53-1.33-1.29-1.69-1.29-1.69-1.06-.73.08-.72.08-.72 1.18.08 1.79 1.22 1.79 1.22 1.05 1.79 2.75 1.28 3.42.98.11-.78.41-1.28.74-1.58-2.55-.29-5.22-1.28-5.22-5.71 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.04 0 0 .97-.31 3.18 1.18a11.2 11.2 0 0 1 2.9-.39c.99 0 1.99.13 2.9.39 2.2-1.5 3.18-1.18 3.18-1.18.63 1.58.23 2.75.11 3.04.74.81 1.19 1.84 1.19 3.1 0 4.44-2.69 5.42-5.25 5.71.42.37.8 1.09.8 2.21 0 1.6-.01 2.89-.01 3.28 0 .31.21.68.8.56A10.53 10.53 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5Z"
          />
        </svg>
      </a>
      <LightDarkModeSwitch />
    </div>
  );
}
